const writeFieldImpl = (obj, field, value) => {
    if (!Array.isArray(field)) {
        if (typeof field === 'string') {
            return writeFieldImpl(obj, field.split('.'), value);
        } else {
            return Object.entries(field).reduce((res, [dest, src]) => {
                const value = readField(obj, src);
                writeFieldImpl(obj, dest, value);
                writeFieldImpl(res, dest, value);
                return res;
            }, {});
        }
    }
    const f = field[0];
    if (field.length > 1) {
        if (!obj[f]) {
            obj[f] = {};
        }
        return writeFieldImpl(obj[f], field.slice(1), value);
    }
    return obj[f] = value;
}

export const writeField = (obj, field, value) => {
    if (typeof field === 'string' && !field.includes('.')) {
        return obj[field] = value;
    }
    return writeFieldImpl(obj, field, value);
}

const readFieldImpl = (obj, field) => {
    if (field && !Array.isArray(field)) {
        if (typeof field === 'string') {
            return readFieldImpl(obj, field.split('.'))
        } else {
            return Object.entries(field).reduce((value, [dest, src]) => {
                writeField(value, dest, readFieldImpl(obj, src));
                return value;
            }, {});
        }
    }
    if (obj && field?.length > 0) {
        if (Array.isArray(obj)) {
            return obj.flatMap(o => readFieldImpl(o[field[0]], field.slice(1)))
        } else {
            return readFieldImpl(obj[field[0]], field.slice(1))
        }
    }
    return obj;
}

export const readField = (obj, field) => {
    if (typeof field === 'string' && !field.includes('.')) {
        return obj?.[field];
    }
    return readFieldImpl(obj, field);
}

const hasOwnPropertyImpl = (obj, field) => {
    if (field && !Array.isArray(field)) {
        return hasOwnPropertyImpl(obj, field.split('.'))
    }
    if (obj && field?.length > 0) {
        if (Array.isArray(obj)) {
            return obj.every(o => o.hasOwnProperty(field[0]) && hasOwnPropertyImpl(o[field[0]], field.slice(1)));
        } else {
            return obj.hasOwnProperty(field[0]) && hasOwnPropertyImpl(obj[field[0]], field.slice(1));
        }
    }
    return (field?.length ?? 0) === 0;
}

export const hasOwnProperty = (obj, field) => {
    if (typeof field === 'string' && !field.includes('.')) {
        return obj.hasOwnProperty(field);
    }
    return hasOwnPropertyImpl(obj, field);
}

export const deepEquals = (a, b) => {
    if (a === b) return true;

    if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object') {
        return false;
    }

    // Date comparison
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }

    // RegExp comparison
    if (a instanceof RegExp && b instanceof RegExp) {
        return a.toString() === b.toString();
    }

    // Array comparison
    const isArrayA = Array.isArray(a);
    const isArrayB = Array.isArray(b);

    if (isArrayA !== isArrayB) return false;

    if (isArrayA) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (!deepEquals(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }

    // Object comparison
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
        return false;
    }

    const keySetB = new Set(keysB); // faster lookup

    for (const key of keysA) {
        if (!keySetB.has(key)) {
            return false;
        }

        const valA = a[key];
        const valB = b[key];

        // Optional: skip function comparison or compare them by toString() if needed
        if (typeof valA === 'function' && typeof valB === 'function') {
            continue;
        }

        if (!deepEquals(valA, valB)) {
            return false;
        }
    }

    return true;
};

export function deepMerge(target, ...sources) {
    if (!sources.length) return target;
    const source = sources.shift();

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key]) Object.assign(target, { [key]: {} });
                deepMerge(target[key], source[key]);
            } else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }

    return deepMerge(target, ...sources);
}

export function isObject(item) {
    return (item && typeof item === 'object' && !Array.isArray(item));
}
/**
 * Deep-copies a value using JSON semantics (like JSON.parse(JSON.stringify(v))).
 */

export function deepClone(value) {
    return JSON.parse(JSON.stringify(value));
}
